<template>
    <b-table-simple
        small
        bordered
        hover
        responsive
        class="role-matrix-table mb-0"
    >
        <b-thead head-variant="light">
            <b-tr>
                <b-th class="align-bottom" :sticky-column="true">Contact</b-th>
                <b-th
                    v-for="col in columns"
                    :key="col.key"
                    class="text-center align-bottom matrix-col"
                    :title="col.description"
                >
                    <div class="font-weight-bold">{{ col.name }}</div>
                    <div
                        v-if="limitText(col)"
                        class="small font-weight-normal text-muted"
                    >{{ limitText(col) }}</div>
                </b-th>
            </b-tr>
        </b-thead>
        <b-tbody>
            <b-tr
                v-for="contact in contacts"
                :key="contact.id"
            >
                <b-td class="align-middle" :sticky-column="true">
                    <div class="font-weight-bold">{{ contact.full_name || contact.email }}</div>
                    <div class="small text-muted">{{ [contact.job_title, contact.email].filter(Boolean).join(' · ') }}</div>
                </b-td>
                <b-td
                    v-for="col in columns"
                    :key="col.key"
                    class="text-center align-middle"
                >
                    <b-form-checkbox
                        class="d-inline-block mr-n2"
                        :checked="isSelected(col.key, contact.id)"
                        :disabled="isCellDisabled(col, contact.id)"
                        :aria-label="`${contact.full_name || contact.email} - ${col.name}`"
                        @change="toggle(col.key, contact.id, $event)"
                    ></b-form-checkbox>
                </b-td>
            </b-tr>
        </b-tbody>
        <b-tfoot>
            <b-tr>
                <b-th class="align-middle text-right font-size-120" :sticky-column="true">Total Selected</b-th>
                <b-td
                    v-for="col in columns"
                    :key="col.key"
                    class="text-center align-middle"
                >
                    <b-badge :variant="countVariant(col)" class="font-size-120">{{ countFor(col.key) }}</b-badge>
                </b-td>
            </b-tr>
        </b-tfoot>
    </b-table-simple>
</template>

<script>
// a checkbox matrix of contacts (rows) x columns (contact types / roles) -- check a cell to give that person that column
// presentational only: the Formulate input that uses it (FormulateTenantRoleMatrix) owns the value
// value is { column_key: [tu_id, ...] } -- emits 'input' with the whole new value
// min/max per column are enforced by the parent's validation rule -- here we only show them, and stop more boxes
//   being checked in a column once its max is reached

export default {
    name: 'RoleMatrixTable',
    props: {
        contacts: {  // rows: [{ id, full_name, email, job_title }]
            type: Array,
            default: () => [],
        },
        columns: {  // [{ key, name, description, min, max }] - max is null for no limit
            type: Array,
            default: () => [],
        },
        value: {  // { column_key: [tu_id, ...] }
            type: Object,
            default: () => ({}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        normalizedValue() {
            return Object.fromEntries(this.columns.map(col => [col.key, Array.isArray(this.value[col.key]) ? this.value[col.key] : []]));
        },
    },
    methods: {
        isSelected(key, tuId) {
            return this.normalizedValue[key].includes(tuId);
        },
        countFor(key) {
            return this.normalizedValue[key].length;
        },
        hasMax(col) {
            return col.max !== null && col.max !== undefined;
        },
        isCellDisabled(col, tuId) {
            if (this.disabled) return true;
            const atMax = this.hasMax(col) && this.countFor(col.key) >= col.max;
            return atMax && !this.isSelected(col.key, tuId);
        },
        limitText(col) {
            if (col.min && this.hasMax(col)) return col.min === col.max ? `exactly ${col.min}` : `${col.min} to ${col.max}`;
            if (col.min) return `at least ${col.min}`;
            if (this.hasMax(col)) return `at most ${col.max}`;
            return '';
        },
        countVariant(col) {
            const count = this.countFor(col.key);
            if (count < (col.min || 0) || (this.hasMax(col) && count > col.max)) return 'soft-danger';
            return (col.min || this.hasMax(col)) ? 'soft-success' : 'soft-secondary';
        },
        toggle(key, tuId, checked) {
            const current = this.normalizedValue[key];
            const updated = checked ? (current.includes(tuId) ? current : [...current, tuId]) : current.filter(id => id !== tuId);
            this.$emit('input', { ...this.normalizedValue, [key]: updated });
        },
    },
};
</script>

<style lang="scss" scoped>
.role-matrix-table {
    .matrix-col {
        min-width: 5rem;
    }
}
</style>
